import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/style_auth.css";
import { useAuth } from "../context/AuthContext";
import { API } from "../api";
import { hashPassword } from "../utils/password";
import { validateAuthForm, validate2FACode } from "../utils/authValidation";
import useFormData from "../hooks/useFormData";
import useCooldownTimer from "../hooks/useCooldownTimer";
import useTwoFACode from "../hooks/useTwoFACode";
import AuthForm from "../components/auth/AuthForm";
import TwoFAForm from "../components/auth/TwoFAForm";
import AuthContainer from "../components/auth/AuthContainer";
import AuthFormSection from "../components/auth/AuthFormSection";
import AuthToggle from "../components/auth/AuthToggle";
import ForgotPasswordForm from "../components/auth/ForgotPasswordForm";

const FormConnexion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputsRef = useRef([]);
  const defaultForm = location.state?.defaultForm || "signin";
  const { setUser } = useAuth();

  // États principaux
  const [isSingIn, setisSingIn] = useState(defaultForm === "signin");
  const [signUpStep, setSignUpStep] = useState("form");
  const [signInStep, setSignInStep] = useState("form"); // "form", "2fa", "forgotPassword", "forgotPasswordCode", "forgotPasswordReset"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // État pour mot de passe oublié
  const [forgotPasswordCooldown, setForgotPasswordCooldown] = useCooldownTimer(0);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Hooks personnalisés
  const [signUpData, handleSignUpChange, resetSignUpForm] = useFormData({
    last_name: "",
    first_name: "",
    email: "",
    password: "",
    confpassword: "",
    rememberMe: false,
  });

  const [signInData, handleSignInChange, resetSignInForm] = useFormData({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [signinCooldown, setSigninCooldown] = useCooldownTimer(0);
  const [signupCooldown, setSignupCooldown] = useCooldownTimer(0);
  const { code: twoFACode, updateCode: setTwoFACode, getCodeString } = useTwoFACode();

  useEffect(() => {
    setisSingIn(location.state?.defaultForm === "signin");
  }, [location.state]);

  const toggleForm = () => {
    setisSingIn(!isSingIn);
    setError("");
  };

  const getCurrentMode = () => {
    if (signUpStep === "2fa" || signInStep === "2fa") return "2fa";
    return isSingIn ? "signin" : "signup";
  };

  const connexionSuccessful = (data) => {
    setUser(data);
    resetSignInForm();
    resetSignUpForm();
    setError("");
    navigate("/"); // modifier par un systeme de redirection a l endroit ou l utilisateur était avant la connexion \\
  };

  const handleAuth = async () => {
    setError("");
    setLoading(true);
    
    try {
      const mode = getCurrentMode();
      const inputData = mode === "signin" ? signInData : signUpData;
      
      const validation = validateAuthForm(inputData, mode);
      if (!validation.valid) {
        setError(validation.msg);
        setLoading(false);
        return;
      }

      const hashedPassword = await hashPassword(inputData.password);
      const dataForApi = { ...inputData, password: hashedPassword };
      delete dataForApi.confpassword;

      const apiEndpoint = mode === "signin" ? "auth" : "auth/create";
      const apiResponse = await API.post(apiEndpoint, dataForApi);

      if (apiResponse.success) connexionSuccessful(apiResponse.data);

      if (apiResponse.twoFA) {
        sessionStorage.setItem("userTempEmail", dataForApi.email);
        sessionStorage.setItem("userTempPassword", dataForApi.password);
        sessionStorage.setItem("userTempRememberMe", dataForApi.rememberMe);
        window.dispatchEvent(new Event("storage"));
        mode === "signin" ? setSignInStep("2fa") : setSignUpStep("2fa");
      }
    } catch (err) {
      setError(err.message || "Erreur inattendue");
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (mode) => {
    setError("");
    setLoading(true);

    try {
      const email = sessionStorage.getItem("userTempEmail");
      const password = sessionStorage.getItem("userTempPassword");
      const rememberMe = sessionStorage.getItem("userTempRememberMe") === "true";
      
      if (!email || !password) {
        setError("Anomalie lors de la vérification du code 2FA. Veuillez vous reconnecter.");
        return;
      }

      const a2f_code = getCodeString();
      const validation = validate2FACode(a2f_code);
      
      if (!validation.valid) {
        setError(validation.msg);
        return;
      }

      const apiResponse = await API.post("auth", { email, password, a2f_code, rememberMe });
      
      if (apiResponse.success) {
        sessionStorage.removeItem("userTempEmail");
        sessionStorage.removeItem("userTempPassword");
        sessionStorage.removeItem("userTempRememberMe");
        window.dispatchEvent(new Event("storage"));
        setTwoFACode(Array(6).fill(""));
        mode === "signin" ? setSignInStep("form") : setSignUpStep("form");
        connexionSuccessful(apiResponse.data);
      }
    } catch (err) {
      setError(err.message);
    } finally { 
      setLoading(false);
    }
  };

  const handleResend2FA = async (mode) => {
    const email = sessionStorage.getItem("userTempEmail");
    if (!email) return;

    const isSignin = mode === "signin";
    const currentCooldown = isSignin ? signinCooldown : signupCooldown;
    const setCooldown = isSignin ? setSigninCooldown : setSignupCooldown;

    if (currentCooldown > 0) return;
    setCooldown(60);
    setTwoFACode(Array(6).fill(""));

    try {
      const apiResponse = await API.post("auth/resend", { email });
      if (!apiResponse.success) setError(apiResponse.error || "Erreur lors de l'envoi du code 2FA");
    } catch (err) {
      const backend = err.response?.data;
      if (err.response?.status === 429) {
        const wait = backend?.waitTime || 60;
        setCooldown(wait);
      } else {
        setError(backend?.error || err.message || "Erreur inattendue");
      }
    }
  };

  const AUTH_MESSAGES = {
    signin: "Utilisez votre email et mot de passe pour vous connecter",
    signup: "Utilisez votre email et mot de passe pour vous inscrire"
  };

  const handleForgotPasswordClick = () => {
    setSignInStep("forgotPassword");
    setError("");
    // Pré-remplir l'email du formulaire signin
    setForgotPasswordData({ 
      email: signInData.email || "", 
      newPassword: "", 
      confirmPassword: "" 
    });
  };

  const handleBackFromForgotPassword = () => {
    setSignInStep("form");
    setError("");
    setForgotPasswordData({ email: "", newPassword: "", confirmPassword: "" });
    setTwoFACode(Array(6).fill(""));
  };

  const handleForgotPasswordPasswordChange = (field, value) => {
    setForgotPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleForgotPasswordSubmit = async (step) => {
    setError("");
    setLoading(true);

    const email = forgotPasswordData.email;
    const fail = (msg) => {
      setError(msg);
      throw new Error(msg);
    };

    try {
      switch (step) {
        case "email": {
          if (!email) fail("Veuillez entrer votre adresse email");

          const { success, error } = await API.post("auth/forgot-password/send_code", { email });
          if (!success) fail(error || "Erreur lors de l'envoi du code");

          setSignInStep("forgotPasswordCode");
          break;
        }

        case "code": {
          const codeString = twoFACode.join("");
          const validation = validate2FACode(codeString);
          if (!validation.valid) fail(validation.msg);
          const { success, error } = await API.post("auth/forgot-password/pairing", { email, fpg_code: codeString, });
          if (!success) fail(error || "Erreur lors de la vérification du code");

          sessionStorage.setItem("fgpTempCode", codeString);
          setSignInStep("forgotPasswordReset");
          break;
        }

        case "reset": {
          const fgpCode = sessionStorage.getItem("fgpTempCode");
          if (!fgpCode) {
            handleBackFromForgotPassword();
            fail("Anomalie lors de la réinitialisation. Veuillez recommencer le processus. [ERP:3]");
          }

          const { newPassword, confirmPassword } = forgotPasswordData;

          if (!newPassword || !confirmPassword) fail("Veuillez remplir tous les champs");
          if (newPassword !== confirmPassword) fail("Les mots de passe ne correspondent pas");

          const hashedPassword = await hashPassword(newPassword);
          const { success, error } = await API.post("auth/forgot-password/update", { email, fpg_code: fgpCode, password: hashedPassword, });

          if (!success) {
            handleBackFromForgotPassword();
            fail(error || "Erreur lors de la mise à jour du mot de passe");
          }

          setTwoFACode(Array(6).fill(""));
          handleBackFromForgotPassword();
          break;
        }

        default:
          fail("Étape inconnue");
      }
    } catch (err) {
      if (!err.silent) setError(err.message || "Erreur inattendue");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordResend = async () => {
    const email = forgotPasswordData.email;
    if (!email) return;

    if (forgotPasswordCooldown > 0) return;
    setForgotPasswordCooldown(60);

    try {
      const APIrep = await API.post("auth/forgot-password/resend-code", { email });
      if (!APIrep.success) setError(APIrep.error || "Erreur lors de l'envoi du code");

    } catch (err) {
      const backend = err.response?.data;
      if (err.response?.status === 429) {
        setForgotPasswordCooldown(backend?.waitTime || 60);
      } else {
        setError(backend?.error || err.message || "Erreur inattendue");
      }
    }
  };

  return (
    <AuthContainer isSignIn={isSingIn}>
      {/* INSCRIPTION */}
      <AuthFormSection type="sign-up" isActive={isSingIn} step={signUpStep}>
        {{
          form: (
            <AuthForm
              mode="signup"
              data={signUpData}
              onChange={handleSignUpChange}
              error={error}
              loading={loading}
              onSubmit={handleAuth}
              message={AUTH_MESSAGES.signup}
            />
          ),
          twofa: (
            <TwoFAForm
              code={twoFACode}
              onCodeChange={setTwoFACode}
              inputsRef={inputsRef}
              loading={loading}
              cooldown={signupCooldown}
              onResend={() => handleResend2FA("signup")}
              onSubmit={() => handle2FA("signup")}
              onBack={() => setSignUpStep("form")}
            />
          )
        }}
      </AuthFormSection>

      {/* CONNEXION */}
      <AuthFormSection type="sign-in" isActive={isSingIn} step={signInStep}>
        {{
          form: (
            <AuthForm
              mode="signin"
              data={signInData}
              onChange={handleSignInChange}
              error={error}
              loading={loading}
              onSubmit={handleAuth}
              message={AUTH_MESSAGES.signin}
              onForgotPassword={handleForgotPasswordClick}
            />
          ),
          twofa: (
            <TwoFAForm
              code={twoFACode}
              onCodeChange={setTwoFACode}
              inputsRef={inputsRef}
              loading={loading}
              cooldown={signinCooldown}
              onResend={() => handleResend2FA("signin")}
              onSubmit={() => handle2FA("signin")}
              onBack={() => setSignInStep("form")}
            />
          ),
          forgotPassword: (
            <ForgotPasswordForm
              step="email"
              email={forgotPasswordData.email}
              onEmailChange={(value) =>
                setForgotPasswordData(prev => ({ ...prev, email: value }))
              }
              code={twoFACode}
              onCodeChange={setTwoFACode}
              inputsRef={inputsRef}
              newPassword={forgotPasswordData.newPassword}
              confirmPassword={forgotPasswordData.confirmPassword}
              onPasswordChange={handleForgotPasswordPasswordChange}
              loading={loading}
              cooldown={forgotPasswordCooldown}
              onResend={handleForgotPasswordResend}
              onSubmit={(step) => handleForgotPasswordSubmit("email")}
              onBack={handleBackFromForgotPassword}
            />
          ),
          forgotPasswordCode: (
            <ForgotPasswordForm
              step="code"
              email={forgotPasswordData.email}
              onEmailChange={(value) =>
                setForgotPasswordData(prev => ({ ...prev, email: value }))
              }
              code={twoFACode}
              onCodeChange={setTwoFACode}
              inputsRef={inputsRef}
              newPassword={forgotPasswordData.newPassword}
              confirmPassword={forgotPasswordData.confirmPassword}
              onPasswordChange={handleForgotPasswordPasswordChange}
              loading={loading}
              cooldown={forgotPasswordCooldown}
              onResend={handleForgotPasswordResend}
              onSubmit={(step) => handleForgotPasswordSubmit("code")}
              onBack={handleBackFromForgotPassword}
            />
          ),
          forgotPasswordReset: (
            <ForgotPasswordForm
              step="reset"
              email={forgotPasswordData.email}
              onEmailChange={(value) =>
                setForgotPasswordData(prev => ({ ...prev, email: value }))
              }
              code={twoFACode}
              onCodeChange={setTwoFACode}
              inputsRef={inputsRef}
              newPassword={forgotPasswordData.newPassword}
              confirmPassword={forgotPasswordData.confirmPassword}
              onPasswordChange={handleForgotPasswordPasswordChange}
              loading={loading}
              cooldown={forgotPasswordCooldown}
              onResend={handleForgotPasswordResend}
              onSubmit={(step) => handleForgotPasswordSubmit("reset")}
              onBack={handleBackFromForgotPassword}
            />
          )
        }}
      </AuthFormSection>

      {/* TOGGLE */}
      <AuthToggle onToggle={toggleForm} />
    </AuthContainer>
  );
};

export default FormConnexion;